import {Avatar} from 'components/DSYS/Avatar';
import {ButtonLink} from 'components/DSYS/Link';
import {
  $applyNodeReplacement,
  DecoratorNode,
  type DOMConversionMap,
  type DOMConversionOutput,
  type DOMExportOutput,
  type LexicalNode,
  type NodeKey,
  type SerializedLexicalNode,
  type Spread,
} from 'lexical';
import {type ReactNode} from 'react';

export type SerializedPersonChipNode = Spread<
  {
    ldap: string;
    name: string;
  },
  SerializedLexicalNode
>;

/** Matches a `/people/{ldap}` profile route, capturing the ldap. */
const PERSON_HREF_PATTERN = /^\/people\/([^/?#]+)/;

/** Profile route a person chip links to. */
const personChipHref = (ldap: string): string => `/people/${ldap}`;

function $convertPersonAnchor(domNode: HTMLElement): DOMConversionOutput {
  const href = domNode.getAttribute('href') ?? '';
  const ldap = PERSON_HREF_PATTERN.exec(href)?.[1] ?? '';
  const name = domNode.textContent?.trim() || ldap;
  return {node: $createPersonChipNode(ldap, name)};
}

/**
 * Compact person reference rendered inside answer tables: a transparent DIG
 * button with the person's avatar, linking to their profile in a new tab.
 * The avatar resolves its photo from the `ldap` alone (initials fall back to
 * `name`), so no directory lookup is needed.
 */
const PersonChip = ({ldap, name}: {ldap: string; name: string}) => (
  <ButtonLink
    to={personChipHref(ldap)}
    target="_blank"
    variant="opacity"
    size="small"
    withIconStart={<Avatar size="xsmall" user={{name, ldap}} />}
  >
    {name}
  </ButtonLink>
);

/**
 * Inline decorator node for a person reference. Built programmatically by
 * {@link MarkdownView} when a table cell links to `/people/{ldap}`; degrades to
 * a plain link when chip data is unavailable.
 */
export class PersonChipNode extends DecoratorNode<ReactNode> {
  __ldap: string;
  __name: string;

  static getType(): string {
    return 'person-chip';
  }

  static clone(node: PersonChipNode): PersonChipNode {
    return new PersonChipNode(node.__ldap, node.__name, node.__key);
  }

  constructor(ldap: string, name: string, key?: NodeKey) {
    super(key);
    this.__ldap = ldap;
    this.__name = name;
  }

  isInline(): boolean {
    return true;
  }

  createDOM(): HTMLElement {
    const span = document.createElement('span');
    span.style.display = 'inline-flex';
    span.style.verticalAlign = 'middle';
    return span;
  }

  updateDOM(): boolean {
    return false;
  }

  static importDOM(): DOMConversionMap | null {
    return {
      a: (domNode: HTMLElement) => {
        const href = domNode.getAttribute('href') ?? '';
        if (!PERSON_HREF_PATTERN.test(href)) {
          return null;
        }
        return {conversion: $convertPersonAnchor, priority: 2};
      },
    };
  }

  exportDOM(): DOMExportOutput {
    const element = document.createElement('a');
    element.setAttribute('href', personChipHref(this.__ldap));
    element.textContent = this.__name;
    return {element};
  }

  static importJSON(serializedNode: SerializedPersonChipNode): PersonChipNode {
    return $createPersonChipNode(serializedNode.ldap, serializedNode.name);
  }

  exportJSON(): SerializedPersonChipNode {
    return {
      ldap: this.__ldap,
      name: this.__name,
      type: 'person-chip',
      version: 1,
    };
  }

  getTextContent(): string {
    return this.getLatest().__name;
  }

  decorate(): ReactNode {
    return <PersonChip ldap={this.__ldap} name={this.__name} />;
  }
}

export function $createPersonChipNode(ldap: string, name: string): PersonChipNode {
  return $applyNodeReplacement(new PersonChipNode(ldap, name));
}

export function $isPersonChipNode(node: LexicalNode | null | undefined): node is PersonChipNode {
  return node instanceof PersonChipNode;
}
